import { Brain, Code, GitBranch, Lightbulb, ArrowLeft, MessageSquarePlus, Workflow, Users, FileEdit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChatMode } from "./chat-mode-selector";

interface ExampleQuery {
  icon: React.ReactNode;
  title: string;
  query: string;
  accentColor: string;
  borderHover: string;
}

const GENERAL_EXAMPLES: ExampleQuery[] = [
  {
    icon: <Brain className="w-4 h-4" />,
    title: "Functional Ontology",
    query: "What are all the personas and their primary outcomes?",
    accentColor: "text-emerald-400",
    borderHover: "hover:border-emerald-500/50",
  },
  {
    icon: <Code className="w-4 h-4" />,
    title: "Code Ontology",
    query: "Show me all API endpoints and their functions",
    accentColor: "text-blue-400",
    borderHover: "hover:border-blue-500/50",
  },
  {
    icon: <GitBranch className="w-4 h-4" />,
    title: "Cross-Functional Tracing",
    query: "How does the login UI connect to backend services?",
    accentColor: "text-violet-400",
    borderHover: "hover:border-violet-500/50",
  },
  {
    icon: <Lightbulb className="w-4 h-4" />,
    title: "Impact Analysis",
    query: "What would be impacted by adding forgot password feature?",
    accentColor: "text-amber-400",
    borderHover: "hover:border-amber-500/50",
  },
];

const REQUIREMENT_EXAMPLES: ExampleQuery[] = [
  {
    icon: <MessageSquarePlus className="w-4 h-4" />,
    title: "Add New Feature",
    query: "I want to add a two-factor authentication feature for user login",
    accentColor: "text-emerald-400",
    borderHover: "hover:border-emerald-500/50",
  },
  {
    icon: <Users className="w-4 h-4" />,
    title: "Define New Persona",
    query: "We need a new Admin persona who can manage user permissions",
    accentColor: "text-blue-400",
    borderHover: "hover:border-blue-500/50",
  },
  {
    icon: <Workflow className="w-4 h-4" />,
    title: "Describe User Journey",
    query: "The user should be able to reset their password via email verification",
    accentColor: "text-violet-400",
    borderHover: "hover:border-violet-500/50",
  },
  {
    icon: <FileEdit className="w-4 h-4" />,
    title: "Update Existing Flow",
    query: "Add an optional phone verification step to the signup process",
    accentColor: "text-amber-400",
    borderHover: "hover:border-amber-500/50",
  },
];

const GENERAL_QUICK_QUERIES = [
  "List all scenarios for the Admin persona",
  "Which services does the Dashboard depend on?",
  "Find functions handling authentication",
  "Map UI screens to API endpoints",
];

const REQUIREMENT_QUICK_QUERIES = [
  "Add email notifications for order updates",
  "Users should be able to export data as CSV",
  "Add dark mode support to the app",
  "Implement session timeout after 30 minutes",
];

interface ChatWelcomeScreenProps {
  onQuerySelect: (query: string) => void;
  onDismiss?: () => void;
  hasExistingMessages?: boolean;
  chatMode?: ChatMode;
}

export default function ChatWelcomeScreen({
  onQuerySelect,
  onDismiss,
  hasExistingMessages = false,
  chatMode = ChatMode.GENERAL,
}: ChatWelcomeScreenProps) {
  const isRequirementMode = chatMode === ChatMode.REQUIREMENT;
  const examples = isRequirementMode ? REQUIREMENT_EXAMPLES : GENERAL_EXAMPLES;
  const quickQueries = isRequirementMode ? REQUIREMENT_QUICK_QUERIES : GENERAL_QUICK_QUERIES;

  const headerText = isRequirementMode
    ? "Describe new features naturally to update your functional graph"
    : "Explore your project's ontology with natural language queries";

  return (
    <div className="flex flex-col items-center justify-center px-4 py-8 max-w-2xl mx-auto w-full">
      {/* Back button */}
      {hasExistingMessages && onDismiss && (
        <div className="w-full flex justify-end mb-4">
          <Button
            onClick={onDismiss}
            variant="ghost"
            size="sm"
            className="text-muted hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Chat
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-4">
        <h1 className="text-2xl font-semibold text-white mb-2">
          BREEZE<span className="text-primary">.AI</span> Assistant
        </h1>
        <p className="text-base text-muted">
          {headerText}
        </p>
      </div>

      {/* Example Cards */}
      <div className="grid grid-cols-2 gap-4 w-full mb-4">
        {examples.map((example, index) => (
          <button
            key={index}
            onClick={() => onQuerySelect(example.query)}
            className={`text-left p-5 rounded-lg border border-surface-border bg-surface-card/50 hover:bg-surface-card ${example.borderHover} transition-all group`}
          >
            <div className="flex items-center gap-2.5 mb-2">
              <span className={example.accentColor}>{example.icon}</span>
              <span className={`text-base font-medium ${example.accentColor}`}>
                {example.title}
              </span>
            </div>
            <p className="text-sm text-muted group-hover:text-white/70 leading-relaxed">
              {example.query}
            </p>
          </button>
        ))}
      </div>

      {/* Quick Queries */}
      <div className="w-full">
        <p className="text-sm text-muted/60 mb-3 text-center uppercase tracking-wide">
          More examples
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {quickQueries.map((query, index) => (
            <button
              key={index}
              onClick={() => onQuerySelect(query)}
              className="text-sm px-4 py-2 rounded-md border border-surface-border/60 text-muted hover:text-white hover:border-primary/30 transition-colors"
            >
              {query}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
